/**
 * RateLimitInlineMessage Component
 * ChatGPT-style inline rate limit message displayed in the chat feed.
 * Shows a countdown timer and an upgrade CTA button.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';

interface RateLimitInlineMessageProps {
  resetAt: string | null;
  onUpgradePress: () => void;
  onCountdownEnd: () => void;
}

/**
 * Compute the number of whole minutes remaining until resetAt.
 * Returns 0 if resetAt is in the past or null.
 */
function getMinutesRemaining(resetAt: string | null): number {
  if (!resetAt) return 0;
  const diff = new Date(resetAt).getTime() - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / 60000);
}

export default function RateLimitInlineMessage({
  resetAt,
  onUpgradePress,
  onCountdownEnd,
}: RateLimitInlineMessageProps) {
  const [minutesLeft, setMinutesLeft] = useState(() => getMinutesRemaining(resetAt));

  const tick = useCallback(() => {
    const remaining = getMinutesRemaining(resetAt);
    setMinutesLeft(remaining);
    if (remaining <= 0) {
      onCountdownEnd();
    }
  }, [resetAt, onCountdownEnd]);

  // Recalculate when resetAt changes
  useEffect(() => {
    setMinutesLeft(getMinutesRemaining(resetAt));
  }, [resetAt]);

  // Tick every 30 seconds for responsive countdown
  useEffect(() => {
    const interval = setInterval(tick, 30000);
    return () => clearInterval(interval);
  }, [tick]);

  const countdownText = minutesLeft > 0
    ? `${minutesLeft} min`
    : 'quelques instants';

  return (
    <View style={styles.wrapper}>
      <View style={styles.container}>
        <View style={styles.iconRow}>
          <Icon name="clock-outline" size={18} color={colors.warning} />
        </View>

        <Text style={styles.messageText}>
          Tu as atteint ta limite de messages gratuits. Réessaie dans{' '}
          <Text style={styles.countdownHighlight}>{countdownText}</Text>
          {' '}ou passe en Premium pour des messages illimités.
        </Text>

        <TouchableOpacity
          style={styles.upgradeButton}
          onPress={onUpgradePress}
          activeOpacity={0.7}
        >
          <Icon name="crown-outline" size={16} color={colors.primary} />
          <Text style={styles.upgradeButtonText}>Passer en Premium</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    alignItems: 'center',
  },
  container: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.border,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    maxWidth: 400,
    width: '100%',
    alignItems: 'center',
  },
  iconRow: {
    marginBottom: spacing.sm,
  },
  messageText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: typography.fontSize.sm * 1.5,
    marginBottom: spacing.md,
  },
  countdownHighlight: {
    fontWeight: typography.fontWeight.semibold,
    color: colors.warning,
  },
  upgradeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.primary,
    backgroundColor: 'transparent',
  },
  upgradeButtonText: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.primary,
  },
});
